import { calculatePromoDeduct } from "./common";

/**
 * Unified order total calculation.
 *
 * Single source of truth for all order math (checkout + instant buy).
 * Edit here when VAT rates, shipping logic, or discount rules change.
 *
 * @param {Object} params
 * @param {Array}  params.products       - Cart items [{product, qty, barcode, ...}]
 * @param {number} [params.country=231]  - Country ID (231 = UAE, shows VAT separately)
 * @param {number} [params.shippingValue=0]
 * @param {boolean} [params.hasFreeShipping=false]
 * @param {boolean} [params.fixedShipping=false]
 * @param {number}  [params.fixedShippingValue=0]
 * @param {Object}  [params.promo=null]
 * @param {boolean} [params.isPromoApplied=false]
 * @param {boolean} [params.isVoucher=false]
 * @param {number}  [params.pointsTotal=0]
 * @returns {Object}
 * @returns {number}  .subtotal       – Net amount before VAT
 * @returns {number}  .vat            – Total VAT amount
 * @returns {number}  .total          – Grand total after all adjustments
 * @returns {string}  .totalBeforeVAT – Formatted total before VAT (country-aware)
 * @returns {string}  .totalAfterVAT  – Formatted total after VAT (country-aware)
 * @returns {number}  .amountToDeduct – Promo/voucher deduction applied
 * @returns {number}  .shippingValue  – Final shipping cost (may be overridden by fixedShipping)
 * @returns {number}  .productsPricesBeforeDiscount – Gross prices before any discount
 * @returns {number}  .productsVATs   – VAT on the products only (0 for non-UAE)
 */
export function calculateOrderTotals({
  products = [],
  country = 231,
  shippingValue = 0,
  hasFreeShipping = false,
  fixedShipping = false,
  fixedShippingValue = 0,
  promo = null,
  isPromoApplied = false,
  isVoucher = false,
  pointsTotal = 0,
} = {}) {
  // ---- Step 1: Subtotal & VAT per product ----
  let subtotal = 0;
  let vat = 0;
  let productsPricesBeforeDiscount = 0;
  let productsVATs = 0;

  products.forEach((prod) => {
    const quantity = prod?.qty || 1;
    const rawPrice = prod?.product?.attributes?.Price || 0;
    const discountedPrice = prod?.product?.attributes?.discountedPrice;
    const productPrice = discountedPrice != null ? discountedPrice : rawPrice;

    productsPricesBeforeDiscount += rawPrice * quantity;

    subtotal += productPrice * quantity;

    if (country === 231) {
      const priceWithoutVAT = productPrice / 1.05;
      const vatAmount = productPrice - priceWithoutVAT;
      vat += vatAmount * quantity;
      productsVATs += vatAmount * quantity;
    }
  });

  // ---- Step 2: Shipping ----
  let effectiveShipping = shippingValue;

  if (hasFreeShipping) {
    effectiveShipping = 0;
  } else if (!hasFreeShipping && fixedShipping) {
    effectiveShipping = parseFloat(fixedShippingValue);
    shippingValue = effectiveShipping;
  }

  // ---- Step 3: Total so far ----
  let total = subtotal + effectiveShipping;

  // ---- Step 4: Promo / Voucher ----
  let amountToDeduct = 0;
  if (isPromoApplied && promo) {
    amountToDeduct = parseFloat(calculatePromoDeduct(promo, subtotal));

    if (isVoucher) {
      if (parseFloat(amountToDeduct) >= total) {
        amountToDeduct = total;
      }
      if (parseFloat(shippingValue) === 0 && !hasFreeShipping) {
        amountToDeduct = 0;
      }
    }
  }

  total -= parseFloat(amountToDeduct);
  if (total <= 0) total = 0;

  // ---- Step 5: Points ----
  if (parseFloat(pointsTotal) > 0 && total > 0) {
    if (parseFloat(pointsTotal) >= total) total = 0;
    else total -= parseFloat(pointsTotal);
  }

  // ---- Step 6: VAT-presentation flags (country-aware) ----
  const totalBeforeVAT =
    country === 231 ? (total - vat).toFixed(2) : total.toFixed(2);
  const totalAfterVAT = total.toFixed(2);

  return {
    subtotal: country === 231 ? parseFloat((subtotal - vat).toFixed(2)) : parseFloat(subtotal.toFixed(2)),
    vat: country === 231 ? parseFloat(vat.toFixed(2)) : 0,
    total: parseFloat(total.toFixed(2)),
    totalBeforeVAT,
    totalAfterVAT,
    amountToDeduct: parseFloat(amountToDeduct),
    shippingValue,
    productsPricesBeforeDiscount,
    productsVATs,
  };
}
